import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { debounce } from 'lodash';
import { DragDropContext } from '@hello-pangea/dnd';

import { updatePractice } from 'api/Practice';

import useDebounce from 'lib/hooks/useDebounce';
import { iaRa, isEqual, optionify } from 'lib/helpers/utility';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';
import {
  useInfiniteSpecialtyCodes,
  useSpecialtyCodes
} from 'lib/hooks/queries/codes/useSpecialtyCodes';

import Icon from 'components/shared/Icon/Icon';
import Input from 'components/shared/Input/Input';

import SpecialtyList from './components/SpecialtyList';

const PracticeSpecialty = ({
  addFooterActions,
  setHeaderButtonProps,
  mutateOrchestratorData,
  onNext,
  orchestratorLoading
}) => {
  const { practice } = useOutletContext();
  const navigate = useNavigate();

  const [specialtyCodes, setSpecialtyCodes] = useState(practice.specialty_code);
  const [selectedSpecialties, setSelectedSpecialties] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const scrollRef = useRef(null);

  const debouncedSearch = useDebounce(searchQuery, 300);

  // Fetch all codes once (cached) to resolve initial selected specialty names
  const { data: allCodesData } = useSpecialtyCodes({
    params: { kind: practice.kind },
    options: { enabled: !!practice.specialty_code?.length }
  });

  // Seed selected specialties from the full codes list on first load
  useEffect(() => {
    if (
      allCodesData?.benefit_type_code?.length &&
      practice.specialty_code?.length &&
      selectedSpecialties.length === 0
    ) {
      const allOptions = optionify(allCodesData.benefit_type_code, 'name', 'code');
      const initial = practice.specialty_code
        .map((code) => allOptions.find((s) => s.value === code))
        .filter(Boolean);
      setSelectedSpecialties(initial);
    }
  }, [allCodesData, selectedSpecialties, practice.specialty_code]);

  const {
    data: availableData,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    isError
  } = useInfiniteSpecialtyCodes({
    params: {
      searchTerm: debouncedSearch,
      excludeCodes: iaRa(specialtyCodes),
      kind: practice.kind
    },
    dependencies: [debouncedSearch, specialtyCodes]
  });

  // Transform available data to option format and filter out selected codes
  const availableSpecialties = useMemo(() => {
    const options = optionify(availableData, 'name', 'code');
    return options?.filter((s) => !specialtyCodes?.includes(s.value)) || [];
  }, [availableData, specialtyCodes]);

  // Keep selected specialties in sync - store full objects
  const orderedSelectedSpecialties = useMemo(() => {
    return (specialtyCodes || [])
      .map((code) => selectedSpecialties.find((s) => s.value === code))
      .filter(Boolean);
  }, [specialtyCodes, selectedSpecialties]);

  const updatePracticeMutation = useCustomMutation({
    mutationFn: () =>
      updatePractice(navigate, {
        practice_id: practice.id,
        fields: { specialty_code: specialtyCodes }
      }),
    disableAlert: true,
    onSuccess: () => mutateOrchestratorData()
  });

  useEffect(() => {
    setHeaderButtonProps(null);
    addFooterActions([
      {
        text: 'Continue',
        onClick:
          isEqual(practice.specialty_code, specialtyCodes) ||
          (practice.specialty_code === null && specialtyCodes === null)
            ? onNext
            : () => updatePracticeMutation.mutateAsync(),
        isDisabled: updatePracticeMutation?.isLoading || orchestratorLoading
      }
    ]);
  }, [
    specialtyCodes,
    practice.specialty_code,
    updatePracticeMutation?.isLoading,
    orchestratorLoading
  ]);

  const handleAddSpecialty = (code) => {
    const specialty = availableSpecialties.find((s) => s.value === code);
    if (specialty) {
      setSelectedSpecialties((prev) => [...prev, specialty]);
    }
    setSpecialtyCodes((prev) => [...(prev || []), code]);
  };

  const handleRemoveSpecialty = (code) => {
    setSelectedSpecialties((prev) => prev.filter((s) => s.value !== code));
    setSpecialtyCodes((prev) => prev?.filter((c) => c !== code) || []);
  };

  // Create debounced scroll handler with cleanup
  const handleScroll = useMemo(() => {
    const debouncedFn = debounce(() => {
      const div = scrollRef.current;
      if (!div) return;

      const isNearBottom = div.scrollTop + div.clientHeight >= div.scrollHeight - 1;

      if (isNearBottom && !isFetchingNextPage && hasNextPage) {
        fetchNextPage();
      }
    }, 200);

    return debouncedFn;
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  // Cleanup debounced function on unmount or when dependencies change
  useEffect(() => {
    return () => {
      handleScroll.cancel();
    };
  }, [handleScroll]);

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const { source, destination, draggableId } = result;

    const getSpecialtyCode = (id) => {
      if (id.startsWith('available-')) return id.replace('available-', '');
      return id;
    };

    const specialtyCode = getSpecialtyCode(draggableId);

    // Cross-list drag: from available to selected
    if (
      source.droppableId === 'availableSpecialties' &&
      destination.droppableId === 'selectedSpecialties'
    ) {
      const specialty = availableSpecialties.find((s) => s.value === specialtyCode);
      if (specialty) {
        setSelectedSpecialties((prev) => {
          const newSelected = [...prev];
          newSelected.splice(destination.index, 0, specialty);
          return newSelected;
        });
      }
      const newCodes = [...(specialtyCodes || [])];
      newCodes.splice(destination.index, 0, specialtyCode);
      setSpecialtyCodes(newCodes);
      return;
    }

    // Cross-list drag: from selected to available (remove from selected)
    if (
      source.droppableId === 'selectedSpecialties' &&
      destination.droppableId === 'availableSpecialties'
    ) {
      setSelectedSpecialties((prev) => prev.filter((s) => s.value !== specialtyCode));
      setSpecialtyCodes((prev) => prev?.filter((c) => c !== specialtyCode) || []);
      return;
    }

    // Same list reordering: selected specialties
    if (
      source.droppableId === 'selectedSpecialties' &&
      destination.droppableId === 'selectedSpecialties'
    ) {
      const newCodes = [...(specialtyCodes || [])];
      const [removed] = newCodes.splice(source.index, 1);
      newCodes.splice(destination.index, 0, removed);
      setSpecialtyCodes(newCodes);

      const newSelected = [...selectedSpecialties];
      const [removedSpecialty] = newSelected.splice(source.index, 1);
      newSelected.splice(destination.index, 0, removedSpecialty);
      setSelectedSpecialties(newSelected);
    }
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="mx-auto flex h-[calc(100vh-322px)] w-full rounded-lg border border-solid border-[#E9EAEB]">
        <div className="flex w-1/2 flex-shrink-0 flex-col px-2">
          <span className="px-6 pb-1.5 pt-3">All billing specialties</span>
          <div className="mb-3 mt-1.5 h-[1px] w-full bg-[#E9EAEB] content-none" />
          <div className="mb-4 px-4">
            <Input
              icon="search-linear"
              placeholder="Search billing specialties"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <SpecialtyList
            ref={scrollRef}
            droppableId="availableSpecialties"
            specialties={availableSpecialties}
            onAction={handleAddSpecialty}
            actionIcon="add-circle-mono"
            actionColor="primary"
            emptyMessage="No billing specialties available"
            draggableIdPrefix="available"
            isLoading={isLoading}
            isFetchingNextPage={isFetchingNextPage}
            isError={isError}
            onScroll={handleScroll}
          />
        </div>

        <div className="relative h-full w-[1px] flex-shrink-0 bg-[#E9EAEB]">
          <div className="absolute top-1/2 flex-shrink-0 -translate-x-1/2 bg-white py-2">
            <Icon icon="arrow-swap-horizontal-linear" />
          </div>
        </div>

        <div className="flex w-1/2 flex-shrink-0 flex-col px-2">
          <span className="px-6 pb-1.5 pt-3">Added billing specialties</span>
          <div className="mb-3 mt-1.5 h-[1px] w-full bg-[#E9EAEB] content-none" />
          <SpecialtyList
            droppableId="selectedSpecialties"
            specialties={orderedSelectedSpecialties}
            onAction={handleRemoveSpecialty}
            actionIcon="minus-circle-mono"
            actionColor="danger"
            emptyMessage="No billing specialties added"
          />
        </div>
      </div>
    </DragDropContext>
  );
};

export default PracticeSpecialty;
